import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GoogleMap, MarkerF, PolygonF, InfoWindowF } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import axios from "axios";
import Swal from "sweetalert2";

const customIcon = (color, isInside = true) => ({
  path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5-2.5z",
  fillColor: color,
  fillOpacity: isInside ? 1 : 0.6,
  strokeWeight: 2,
  strokeColor: isInside ? "#ffffff" : "#bdc3c7",
  scale: 1.8,
  anchor: { x: 12, y: 22 }
});

const MapPublicPage = () => {
  const { id } = useParams();
  const { isLoaded } = useGoogleMaps();
  const [searchParams] = useSearchParams();

  const mapRef = useRef(null);
  const hasFitBounds = useRef(false);
  const polygonsRef = useRef([]);

  // NEW: State to track if the Map component is fully initialized
  const [mapIsReady, setMapIsReady] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [selectedTop, setSelectedTop] = useState(null);
  const [mainMarkers, setMainMarkers] = useState([]);
  const [googleMarkers, setGoogleMarkers] = useState([]);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);

  // ---------- FILTER PARSER ----------
  const parseFilters = useCallback(() => {
    const p = (key) => searchParams.get(key);
    const hasSource = p("main") !== null || p("google") !== null;
    return {
      showMain: hasSource ? p("main") === "1" : true,
      showGoogle: hasSource ? p("google") === "1" : true,
      showInside: p("inside") !== null ? p("inside") === "1" : true,
      showOutside: p("outside") !== null ? p("outside") === "1" : true,
    };
  }, [searchParams]);

  const filters = useMemo(() => parseFilters(), [parseFilters]);
  const [showMain, setShowMain] = useState(filters.showMain);
  const [showGoogle, setShowGoogle] = useState(filters.showGoogle);
  const [showInside, setShowInside] = useState(filters.showInside);
  const [showOutside, setShowOutside] = useState(filters.showOutside);

  // ---------- FIT MAP TO GOOGLE LOCATIONS ----------
  const fitToGoogleMarkers = useCallback(() => {
    if (hasFitBounds.current || !mapRef.current || googleMarkers.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    googleMarkers.forEach(g => {
      const lat = parseFloat(g.location_lat);
      const lng = parseFloat(g.location_lng);
      if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
      hasFitBounds.current = true;
    }
  }, [googleMarkers]);

  // ---------- DATA LOADING (Triggers only after Map is Ready) ----------
  useEffect(() => {
    const loadData = async () => {
      if (!mapIsReady || !id) return; // Wait for map initialization

      try {
        const areaRes = await axios.get(`/api/public/top-locations/${id}`);
        let areaData = areaRes.data;
        if (typeof areaData.geometrics_outline === "string") {
          areaData.geometrics_outline = JSON.parse(areaData.geometrics_outline);
        }
        setSelectedTop(areaData);

        const [mRes, gRes] = await Promise.all([
          axios.get(`/api/public/main-locations?top_id=${id}`),
          axios.get(`/api/public/google-locations`)
        ]);
        
        setMainMarkers(mRes.data.data || []);
        setGoogleMarkers(gRes.data.data || []);
        setDataLoaded(true); // Signal that rendering can start
      } catch (e) {
        console.error("Fetch error:", e);
      }
    };

    loadData();
  }, [mapIsReady, id]);

  // ---------- CACHE POLYGONS & FIT ----------
  useEffect(() => {
    if (dataLoaded && googleMarkers.length > 0) {
      fitToGoogleMarkers();
    }
    
    if (window.google && selectedTop?.geometrics_outline) {
      polygonsRef.current = selectedTop.geometrics_outline.map(poly => {
        const path = Array.isArray(poly) ? poly : poly.path;
        return new window.google.maps.Polygon({ paths: path });
      });
    }
  }, [dataLoaded, googleMarkers, fitToGoogleMarkers, selectedTop]);

  const checkInside = useCallback((lat, lng) => {
    if (!window.google || !polygonsRef.current.length) return false;
    const pt = new window.google.maps.LatLng(parseFloat(lat), parseFloat(lng));
    return polygonsRef.current.some(poly => window.google.maps.geometry.poly.containsLocation(pt, poly));
  }, []);

  const processedGoogle = useMemo(() => {
    return googleMarkers.map(g => ({
      ...g,
      lat: g.location_lat,
      lng: g.location_lng,
      isInside: checkInside(g.location_lat, g.location_lng)
    }));
  }, [googleMarkers, checkInside]);

  const filteredGoogle = useMemo(() => {
    return processedGoogle.filter(g => (g.isInside && showInside) || (!g.isInside && showOutside));
  }, [processedGoogle, showInside, showOutside]);

  const counts = useMemo(() => {
    const internalInside = mainMarkers.filter(m => checkInside(m.lat, m.lng)).length;
    const googleInside = processedGoogle.filter(g => g.isInside).length;
    return {
      internal: mainMarkers.length,
      google: processedGoogle.length,
      inside: internalInside + googleInside,
      outside: (mainMarkers.length + processedGoogle.length) - (internalInside + googleInside)
    };
  }, [mainMarkers, processedGoogle, checkInside]);

  if (!isLoaded) return null;

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      
      {/* OVERLAY PANEL */}
      <div style={overlayStyle}>
        <div style={headerStyle}>
          <h6 style={{ margin: 0, fontWeight: 800 }}>{selectedTop?.name || "Initializing..."}</h6>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ ...labelStyle, backgroundColor: showMain ? '#e7f1ff' : '#fff', borderLeft: showMain ? '4px solid #0d6efd' : '4px solid #dee2e6' }}>
            <input type="checkbox" checked={showMain} onChange={() => setShowMain(!showMain)} style={checkboxStyle} />
            <span style={{ flex: 1 }}>Internal Database</span>
            <small style={badgeStyle}>{counts.internal}</small>
          </label>

          <label style={{ ...labelStyle, backgroundColor: showGoogle ? '#fff8f0' : '#fff', borderLeft: showGoogle ? '4px solid #ff8c00' : '4px solid #dee2e6' }}>
            <input type="checkbox" checked={showGoogle} onChange={() => setShowGoogle(!showGoogle)} style={checkboxStyle} />
            <span style={{ flex: 1 }}>Google Places</span>
            <small style={badgeStyle}>{counts.google}</small>
          </label>

          <div style={{ height: '1px', background: '#eee', margin: '8px 0' }} />

          <label style={{ ...labelStyle, backgroundColor: showInside ? '#f0fff4' : '#fff', borderLeft: showInside ? '4px solid #22c55e' : '4px solid #dee2e6' }}>
            <input type="checkbox" checked={showInside} onChange={() => setShowInside(!showInside)} style={checkboxStyle} />
            <span style={{ flex: 1 }}>Inside Geofence</span>
            <small style={badgeStyle}>{counts.inside}</small>
          </label>

          <label style={{ ...labelStyle, backgroundColor: showOutside ? '#fff5f5' : '#fff', borderLeft: showOutside ? '4px solid #ef4444' : '4px solid #dee2e6' }}>
            <input type="checkbox" checked={showOutside} onChange={() => setShowOutside(!showOutside)} style={checkboxStyle} />
            <span style={{ flex: 1 }}>Outside Geofence</span>
            <small style={badgeStyle}>{counts.outside}</small>
          </label>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat: 48.13, lng: 11.7 }}
        zoom={13}
        onLoad={(map) => { 
            mapRef.current = map; 
            setMapIsReady(true); // <--- THIS IS THE TRIGGER
        }}
        options={{ disableDefaultUI: true, zoomControl: true, styles: mapTheme }}
      >
        {/* Only render content once data is actually loaded and map is ready */}
        {dataLoaded && (
          <>
            {selectedTop?.geometrics_outline?.map((item, i) => (
              <PolygonF 
                key={i} 
                paths={Array.isArray(item) ? item : item.path} 
                options={{ fillColor: "#0d6efd", fillOpacity: 0.1, strokeColor: "#0d6efd", strokeWeight: 2 }} 
              />
            ))}

            {showMain && mainMarkers.map(m => (
              <MarkerF 
                key={`m-${m.id}`} 
                position={{ lat: parseFloat(m.lat), lng: parseFloat(m.lng) }} 
                icon={customIcon("#0d6efd", true)} 
                onClick={() => setActiveInfoWindow({ ...m, title: m.location_name, type: 'Internal' })} 
              />
            ))}

            {showGoogle && filteredGoogle.map(g => (
              <MarkerF 
                key={`g-${g.place_id}`} 
                position={{ lat: parseFloat(g.lat), lng: parseFloat(g.lng) }} 
                icon={customIcon("#ff8c00", g.isInside)} 
                onClick={() => setActiveInfoWindow({ ...g, title: g.name, type: 'Google' })} 
              />
            ))}

            {activeInfoWindow && (
              <InfoWindowF 
                position={{ lat: parseFloat(activeInfoWindow.lat || activeInfoWindow.location_lat), lng: parseFloat(activeInfoWindow.lng || activeInfoWindow.location_lng) }} 
                onCloseClick={() => setActiveInfoWindow(null)} 
                options={{ disableAutoPan: true }}
              >
                <div style={{ padding: '6px', minWidth: '150px' }}>
                  <small style={{ color: '#888', fontWeight: 800, fontSize: '10px', textTransform: 'uppercase' }}>{activeInfoWindow.type}</small>
                  <div style={{ fontWeight: 700, fontSize: '14px', marginTop: '2px' }}>{activeInfoWindow.title}</div>
                </div>
              </InfoWindowF>
            )}
          </>
        )}
      </GoogleMap>
    </div>
  );
};

/* --- STYLES --- */
const mapTheme = [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }];
const overlayStyle = { position: "absolute", top: "25px", left: "25px", zIndex: 100, width: "260px", backgroundColor: "white", padding: "20px", borderRadius: "16px", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" };
const headerStyle = { marginBottom: '15px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' };
const labelStyle = { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '12px', padding: '10px', borderRadius: '8px', transition: 'all 0.2s', marginBottom: '2px' };
const checkboxStyle = { marginRight: '10px' };
const badgeStyle = { backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', color: '#666', fontWeight: 800, marginLeft: '8px' };

export default MapPublicPage;